/**
 * CommunityHealth — admin dashboard rollup of moderation signals.
 *
 * Rolls up: censor rate, top report reasons, at-risk users (3+ reports),
 * image-safety mix, and live-attendance → course credit conversion.
 *
 * Data: GET /api/admin/community/health?days=N
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import {
  HeartPulse, RefreshCw, AlertTriangle, Flag, Camera, Video, TrendingUp,
  Users, ArrowRight,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const WINDOWS = [
  { v: 7,  l: '7d'  },
  { v: 30, l: '30d' },
  { v: 90, l: '90d' },
];

const REASON_LABEL = {
  spam: 'Spam', harassment: 'Harassment', hate: 'Hate speech',
  sexual: 'Sexual', misinformation: 'Misinformation',
  self_harm: 'Self-harm', other: 'Other',
};

const STATUS_LABEL = {
  safe: 'Safe', uncertain: 'Uncertain', unsafe: 'Unsafe',
  skipped: 'Skipped', unknown: 'Unknown',
};

const STATUS_TONE = {
  safe:      'bg-emerald-500/15 text-emerald-700',
  uncertain: 'bg-amber-500/15 text-amber-700',
  unsafe:    'bg-rose-500/15 text-rose-700',
  skipped:   'bg-slate-500/15 text-slate-600',
  unknown:   'bg-muted text-muted-foreground',
};

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return ''; }
};

const Tile = ({ label, value, sub, tone, icon }) => (
  <div className={`rounded-xl border p-3 ${tone === 'danger'
    ? 'border-rose-300 bg-rose-50/60 dark:bg-rose-900/10'
    : 'border-border bg-muted/40'}`}>
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">
      {icon} {label}
    </div>
    <div className={`text-xl font-black leading-tight ${tone === 'danger' ? 'text-rose-700' : 'text-foreground'}`}>{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

// Simple SVG sparkline — no external chart dep.
const Sparkline = ({ points = [], color = 'currentColor' }) => {
  if (!points.length) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  const w = 200, h = 40, pad = 2;
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="40" className="text-primary">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
};

const CommunityHealth = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/admin/community/health?days=${d}`);
      setData(resp);
    } catch (_) { toast.error('Could not load community health'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  const s = data?.summary || {};
  const reasonMax = Math.max(1, ...(data?.reasons || []).map((r) => r.count));

  return (
    <section className="pro-card p-4 mb-5" data-testid="community-health">
      {/* Header */}
      <div className="flex items-start gap-2 flex-wrap mb-3">
        <div className="h-8 w-8 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
          <HeartPulse className="w-4 h-4" />
        </div>
        <div>
          <div className="eyebrow text-emerald-600">Community Health</div>
          <div className="text-xs text-muted-foreground max-w-lg">
            Moderation pulse: censor rate, top report reasons, at-risk users,
            image safety mix, and live-session credit conversion.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-[10px] font-bold" data-testid="health-window-tabs">
            {WINDOWS.map((w) => (
              <button
                key={w.v}
                type="button"
                onClick={() => setDays(w.v)}
                data-testid={`health-window-${w.v}`}
                className={`px-2.5 h-7 uppercase tracking-widest transition-colors ${
                  days === w.v ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {w.l}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => load(days)} className="rounded-full" data-testid="health-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="health-summary">
        <Tile
          label="Censor rate"
          value={`${s.censor_rate_pct ?? 0}%`}
          sub={`${s.censored_posts ?? 0} of ${s.total_posts ?? 0} posts`}
          tone={(s.censor_rate_pct ?? 0) >= 10 ? 'danger' : undefined}
          icon={<AlertTriangle className="w-3 h-3" />}
        />
        <Tile
          label="Open reports"
          value={s.open_reports ?? 0}
          sub="Awaiting review"
          tone={(s.open_reports ?? 0) >= 5 ? 'danger' : undefined}
          icon={<Flag className="w-3 h-3" />}
        />
        <Tile
          label="At-risk users"
          value={s.at_risk_users ?? 0}
          sub="3+ reports in 90d"
          tone={(s.at_risk_users ?? 0) >= 1 ? 'danger' : undefined}
          icon={<Users className="w-3 h-3" />}
        />
        <Tile
          label="Live credit conv."
          value={`${s.credit_conversion_pct ?? 0}%`}
          sub={`${s.live_credit_grants ?? 0} of ${s.total_attendees ?? 0} attendees`}
          icon={<Video className="w-3 h-3" />}
        />
      </div>

      {/* Two-column: reasons chart + at-risk list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid="health-reasons">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">
            <Flag className="w-3 h-3" /> Report reasons
          </div>
          {(data?.reasons || []).length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">No reports in window.</div>
          )}
          <ul className="space-y-1.5">
            {(data?.reasons || []).map((r) => (
              <li key={r.reason} className="flex items-center gap-2">
                <div className="text-[11px] font-bold text-foreground w-24 truncate">{REASON_LABEL[r.reason] || r.reason}</div>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-rose-500 rounded-full"
                    style={{ width: `${Math.max(4, (r.count / reasonMax) * 100)}%` }}
                  />
                </div>
                <div className="text-[11px] font-bold text-muted-foreground w-8 text-right">{r.count}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid="health-at-risk">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> At-risk users
          </div>
          {(data?.at_risk || []).length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">Nobody past the 3-report threshold.</div>
          )}
          <ul className="space-y-1.5">
            {(data?.at_risk || []).map((u) => (
              <li key={u.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 bg-background/60" data-testid={`at-risk-${u.user_id}`}>
                <div className="h-7 w-7 rounded-full bg-rose-500/15 text-rose-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                  {u.user_avatar
                    ? <img src={u.user_avatar} alt="" className="h-full w-full object-cover" />
                    : (u.user_name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-foreground truncate">{u.user_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {u.report_count} reports · {(u.reasons || []).slice(0, 3).map((r) => REASON_LABEL[r] || r).join(', ')} · {rel(u.last_at)}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/u/${u.user_id}`)} className="rounded-full h-7 text-[10px]">
                  Review <ArrowRight className="w-3 h-3 ml-0.5" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom row: image mix + sparkline */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid="health-image-mix">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">
            <Camera className="w-3 h-3" /> Image safety mix
          </div>
          {(data?.image_mix || []).length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">No images uploaded in window.</div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {(data?.image_mix || []).map((m) => (
              <span key={m.status} className={`inline-flex items-center rounded-full text-[10px] font-bold px-2 py-0.5 ${STATUS_TONE[m.status] || STATUS_TONE.unknown}`}>
                {STATUS_LABEL[m.status] || m.status}: {m.count}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid="health-reports-sparkline">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Reports per day
          </div>
          <Sparkline points={data?.reports_daily || []} />
          <div className="text-[10px] text-muted-foreground text-center mt-1">Last {days} days</div>
        </div>
      </div>
    </section>
  );
};

export default CommunityHealth;
